import { CWElementsConfig, useConfig } from '@cw-elements/config';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';
import { Drawer, Select } from 'antd';
import { useUnitSystemStore } from 'modules/yield-analysis/store/useUnitSystemStore';
import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import ReactJson from 'react-json-view';
import { useFarmsAndFields } from '../hooks/useFarmsAndFields';

interface MachineryFieldOperationsProps {
  getConfigInstance: () => CWElementsConfig;
  userLanguage: string;
  orgId: string;
  fieldId: string;
  farmId: string;
  onClose: () => void;
  unitSystem: string;
  onSelect: (value: any) => void;
  onSelectImage: (value: any) => void;
  hideFilters: boolean;
}

export const MachineryFieldOperations: React.FC<{ orgId: string }> = ({
  orgId,
}: {
  orgId: string;
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [selectedOperation, setSelectedOperation] = useState<unknown>({});
  const [selectedImage, setSelectedImage] = useState<unknown>({});
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');
  const { unitSystem } = useUnitSystemStore();
  const { farms, fields, selectedFarm, setSelectedFarm, loading } = useFarmsAndFields(orgId);

  const MachineryFieldOperations = React.useMemo(
    () =>
      React.lazy<React.FC<MachineryFieldOperationsProps>>(() =>
        loadRemoteModule('machinery-field-operations/index'),
      ),
    [],
  );

  return (
    <div className="gap-8 flex flex-col items-center justify-center">
      <div className="gap-4 flex flex-col items-center justify-center w-360px">
        <div className="w-full">
          <label className="block mb-1">Farm:</label>
          <Select
            className="w-full"
            value={selectedFarm?.id}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => {
              const farm = farms.find((f) => f.id === value);
              setSelectedFarm(farm);
              setSelectedFieldId('');
            }}
            options={farms.map((farm) => ({
              label: farm.name,
              value: farm.id,
            }))}
          />
        </div>

        <div className="w-full">
          <label className="block mb-1">Field:</label>
          <Select
            className="w-full"
            value={selectedFieldId}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => setSelectedFieldId(value)}
            options={fields.map((field) => ({
              label: field.name,
              value: field.id,
            }))}
            allowClear
            onClear={() => setSelectedFieldId('')}
          />
        </div>
      </div>
      <Button
        type={ButtonType.primary}
        onClick={() => setIsModalOpen(true)}
        className="w-100"
        disabled={loading}
        data-testid="button"
      >
        Open Machinery Field Operations
      </Button>

      {selectedOperation && (
        <div className="mt-4">
          <h3>Selected Operation:</h3>
          <div className="border p-4 rounded bg-gray-50 max-h-200px overflow-y-auto">
            <ReactJson
              src={selectedOperation}
              theme="rjv-default"
              collapsed={1}
              displayDataTypes={false}
              enableClipboard={true}
            />
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="mt-4">
          <h3>Selected Image:</h3>
          <div className="border p-4 rounded bg-gray-50 max-h-200px overflow-y-auto">
            <ReactJson
              src={selectedImage}
              theme="rjv-default"
              collapsed={1}
              displayDataTypes={false}
              enableClipboard={true}
            />
          </div>
        </div>
      )}
      <ErrorBoundary fallback={<span />}>
        <React.Suspense fallback={<span />}>
          <Drawer
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            height={'90vh'}
            closable={false}
            bodyStyle={{
              padding: 0,
            }}
          >
            <MachineryFieldOperations
              getConfigInstance={useConfig}
              userLanguage="en"
              onClose={() => setIsModalOpen(false)}
              orgId={orgId}
              farmId={selectedFarm?.id}
              fieldId={selectedFieldId}
              unitSystem={unitSystem.toLowerCase()}
              onSelect={(operation: any) => {
                console.log({ operation });
                setSelectedOperation(operation ?? {});
              }}
              onSelectImage={(image: any) => {
                console.log({ image });
                setSelectedImage(image);
              }}
            />
          </Drawer>
        </React.Suspense>
      </ErrorBoundary>
    </div>
  );
};
